import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { Subject } from 'rxjs';
import * as DOMPurify from 'dompurify';
import {
  AlertsProps,
  AlertData,
  SUCCESS_ALERT,
  WARNING_ALERT,
  INFO_ALERT,
  ERROR_ALERT,
  AlertStatus,
} from './types';
import {
  AlertAction,
  AlertBody,
  AlertBottom,
  AlertContainer,
  AlertContent,
  AlertTitle,
  AlertTop,
  ButtonContainer,
} from './styles';
import Modal from '../Modal/Modal';
import Button from '../Button/Button';
import { ReactComponent as SuccessIcon } from '../assets/ico-ok.svg';
import { ReactComponent as WarningIcon } from '../assets/ico-warning.svg';
import { ReactComponent as ErrorIcon } from '../assets/ico-error.svg';
import { ReactComponent as InfoIcon } from '../assets/ico-info.svg';
import { ThemeContext } from '..';
import { KEYS } from '../utils/constants';

export const $alerts = new Subject<AlertData>();

const Alerts: React.FC<AlertsProps> = () => {
  const [alerts, setAlert] = useState<AlertData>({
    type: null,
    title: '',
  });
  const {
    isMobile,
    type,
    title,
    subMessage,
    onCancelLabel,
    onConfirmLabel,
    closeOnBlur = false,
    testId,
    closeButton = true,
  } = alerts;
  const { actualTheme, isMobile: mobileTheme } = useContext(ThemeContext);
  const useMobileVersion = useMemo(() => (isMobile !== undefined ? isMobile : mobileTheme), [
    isMobile,
    mobileTheme,
  ]);

  const showPadding = useMemo(() => (useMobileVersion && !onCancelLabel) || !useMobileVersion, [
    onCancelLabel,
    useMobileVersion,
  ]);

  const onClose = useCallback(() => {
    $alerts.next({
      type: null,
      title: '',
    });

    if (alerts.onClose) {
      alerts.onClose();
    }
  }, [alerts]);

  const goBackMobileHandler = useCallback(
    (event: any) => {
      event.detail.register(10, () => {
        onClose();
      });
    },
    [onClose],
  );

  useEffect(() => {
    if (type !== null) document.addEventListener('ionBackButton', goBackMobileHandler);
    else document.removeEventListener('ionBackButton', goBackMobileHandler);
  }, [type, goBackMobileHandler]);

  const confirmAction = useCallback(() => {
    onClose();
    if (alerts.onConfirm) {
      alerts.onConfirm();
    }
  }, [alerts, onClose]);

  const cancelAction = useCallback(() => {
    if (alerts.onCancel) {
      alerts.onCancel();
    }

    onClose();
  }, [alerts, onClose]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if ([KEYS.Enter, KEYS.NumEnter].includes(event.code as KEYS)) {
        event.stopImmediatePropagation();
        event.preventDefault();
        confirmAction();
      }
      if (event.code === KEYS.Esc) {
        event.preventDefault();
        event.stopImmediatePropagation();
        onClose();
      }
    },
    [confirmAction, onClose],
  );

  const handleSubMessage = useMemo(
    () => ({ __html: DOMPurify.sanitize(subMessage, { USE_PROFILES: { html: true } }) }),
    [subMessage],
  );
  useEffect(() => {
    if (type !== null) {
      document.addEventListener('keydown', onKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const renderAction = useCallback(() => {
    const confirmStatus = AlertStatus[type];
    return (
      <>
        {onCancelLabel && (
          <ButtonContainer isMobile={useMobileVersion}>
            <Button
              testId={`${testId}-cancel-button`}
              hierarchy="tertiary"
              label={onCancelLabel}
              status={confirmStatus}
              type="button"
              onClick={cancelAction}
              fullWidth
            />
          </ButtonContainer>
        )}
        {onConfirmLabel && (
          <ButtonContainer isMobile={useMobileVersion}>
            <Button
              testId={`${testId}-confirm-button`}
              label={onConfirmLabel}
              status={confirmStatus}
              type="button"
              onClick={confirmAction}
              fullWidth
            />
          </ButtonContainer>
        )}
      </>
    );
  }, [type, onCancelLabel, useMobileVersion, testId, cancelAction, onConfirmLabel, confirmAction]);

  const renderHeader = useCallback(() => {
    const icons = {
      [SUCCESS_ALERT]: <SuccessIcon />,
      [WARNING_ALERT]: <WarningIcon />,
      [INFO_ALERT]: <InfoIcon />,
      [ERROR_ALERT]: <ErrorIcon />,
    };
    return type in icons ? icons[type] : '';
  }, [type]);

  useEffect(() => {
    const subscription = $alerts.subscribe((alertData) => {
      setAlert(alertData);
    });
    return () => subscription.unsubscribe();
  }, []);

  return (
    <ThemeProvider theme={actualTheme}>
      <Modal
        isOpen={type !== null}
        width="592px"
        height="auto"
        onClose={onClose}
        closeOnBlur={closeOnBlur}
        isMobile={useMobileVersion}
        overAll
        testId={testId}
        closeButton={closeButton}
      >
        <AlertContainer id="alerts-id" data-testid={`${testId}-container`}>
          <AlertContent paddingBottom={showPadding}>
            <AlertTop>{renderHeader()}</AlertTop>
            <AlertBottom>
              <AlertTitle data-testId={`${testId}-title`} isMobile={useMobileVersion}>
                {title}
              </AlertTitle>
              <AlertBody
                data-testId={`${testId}-body`}
                isMobile={useMobileVersion}
                dangerouslySetInnerHTML={handleSubMessage}
              />
              <AlertAction data-testId={`${testId}-button-container`} isMobile={useMobileVersion}>
                {renderAction()}
              </AlertAction>
            </AlertBottom>
          </AlertContent>
        </AlertContainer>
      </Modal>
    </ThemeProvider>
  );
};

export default Alerts;
